import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Label } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ProductsService from '@/services/ProductsService';

const FALLBACK_ERROR_KEY = 'productsCombo.errorFallback';

/*

<ProductsCombo
	selectedId={selectedProductId}
	selectedProductName={service} // will add new names to db if needed
	onChange={(product) => { setService(product?.name || ''); setSelectedProductId(product?._id); }}
	placeholder="Select or create a product..."
/>

DONE: 
- all products
- selecting from list
- selectectedId
- createNew -> onChange

*/
export default function ProductsCombo({
	selectedId = '',
	selectedProductName = '',
	onChange,
	createDefaults = null,
	allowCreate = true,
}) {
	const { t } = useTranslation();
	const [query, setQuery] = useState('');
	const [options, setOptions] = useState([]);
	const [selectedProduct, setSelectedProduct] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const fetchProducts = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const { data, error } = await ProductsService.getAllProducts();
			if (error) throw new Error(error);
			const cleaned = Array.isArray(data) ? data.filter(Boolean) : [];
			setOptions(cleaned);
		} catch (err) {
			setError(err.message || FALLBACK_ERROR_KEY);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchProducts();
	}, [fetchProducts]);

	const selectedProductMemo = useMemo(() => {
		if (!selectedId) return null;
		return options.find((p) => p._id === selectedId) || null;
	}, [selectedId, options]);

	const handleAddNew = useCallback(async (productName) => {
		const payload = { name: productName };
		if (createDefaults?.sku != null && createDefaults.sku !== '') {
			payload.sku = createDefaults.sku;
		}
		if (createDefaults?.article_number != null && createDefaults.article_number !== '') {
			payload.article_number = createDefaults.article_number;
		}
		if (createDefaults?.price != null && createDefaults.price !== '') {
			payload.price = createDefaults.price;
		}
		if (createDefaults?.taxPercent != null && createDefaults.taxPercent !== '') {
			payload.taxPercent = createDefaults.taxPercent;
		}

		let {error, data} = await ProductsService.addProduct(payload);
		if(error) {
			setError(error);
		} else {
			setOptions(prev => [...prev, data]);
			setSelectedProduct(data);
			onChange?.(data);
			return;
		}
	}, [createDefaults, onChange]);

	useEffect(() => {
		if (!selectedId) {
			setSelectedProduct(null);
			setQuery('');
			return;
		}
		setSelectedProduct(selectedProductMemo);
	}, [selectedId, selectedProductMemo]);

	useEffect(() => {
		const normalizedName = selectedProductName.trim();
		if (!normalizedName) return;
		const match = options.find((p) => p._id === selectedId) || options.find((p) => p.name === normalizedName);
		if (match) {
			setSelectedProduct(match);
			return;
		}
		setSelectedProduct((current) => {
			if (current?.name === normalizedName && (current?._id || '') === (selectedId || '')) {
				return current;
			}
			return { _id: selectedId || null, name: normalizedName };
		});
	}, [options, selectedId, selectedProductName]);

	const handleChange = (selectedProduct) => {
		if(!selectedProduct || !selectedProduct.name) return; 
		// console.log(`TCL -> ProductsCombo.jsx:39 -> handleChange -> selectedProduct:`, selectedProduct)
		if(selectedProduct._id == null) {
			handleAddNew(selectedProduct.name);
			return;
		}
		// console.log(`Selecting product: `, selectedProduct)
		onChange?.(selectedProduct);
	};

	let disabled = loading;
	const filteredOptions = query === ''
		? options
		: options.filter((option) => option.name.toLowerCase().includes(query.toLowerCase()));

	return (
		<Combobox
			as="div"
			value={selectedProduct}
			onChange={(product) => {
				setQuery('');
				setSelectedProduct(product);
				handleChange(product);
			}}
			disabled={disabled}
		>
			<div className="relative mt-2">
				<ComboboxInput
					className="block w-full rounded-md bg-white py-1.5 pr-12 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm"
					onChange={(event) => setQuery(event.target.value)}
					onBlur={() => { setQuery(''); }}
					displayValue={(product) => product?.name || ''}
					placeholder={t('productsCombo.placeholder')}
				/>
				<ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden">
					<ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
				</ComboboxButton>

				<ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg outline outline-black/5 sm:text-sm">
					{filteredOptions.map((product) => (
						<ComboboxOption
							key={product._id}
							value={product}
							className="cursor-default px-3 py-2 text-gray-900 select-none data-focus:bg-indigo-600 data-focus:text-white data-focus:outline-hidden"
						>
							<span className="block truncate">{product.name}</span>
						</ComboboxOption>
					))}
					{query.length > 0 && allowCreate && (
						<ComboboxOption
							value={{ id: null, name: query }}
							className="cursor-default px-3 py-2 text-gray-900 select-none data-focus:bg-indigo-600 data-focus:text-white data-focus:outline-hidden"
						>
							{t('productsCombo.add', { query })}
						</ComboboxOption>
					)}
				</ComboboxOptions>
			</div>
			{loading && <p className="mt-2 text-sm text-gray-500">{t('productsCombo.loading')}</p>}
			{error && <p className="mt-2 text-sm text-red-600">{t(error)}</p>}
		</Combobox>
	);
}
